import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const TRANSACTIONAL_EMAILS = [
  { id: 'enrollment_confirmation', name: 'Enrollment Confirmation', description: 'Sent when a student successfully enrolls in a course', recipient: 'Student' },
  { id: 'payment_receipt', name: 'Payment Receipt', description: 'Sent after each successful payment', recipient: 'Student' },
  { id: 'payment_failed', name: 'Payment Failed', description: 'Sent when a payment is declined or fails', recipient: 'Student' },
  { id: 'account_created', name: 'Account Created', description: 'Welcome email sent when a student account is created', recipient: 'Student' }
];

const LIFECYCLE_EMAILS = [
  { id: 'class_reminder', name: 'Class Reminder', description: 'Sent 1 day before a scheduled class', recipient: 'Student' },
  { id: 'payment_reminder', name: 'Payment Reminder', description: 'Sent 7 days before subscription renewal', recipient: 'Student' },
  { id: 'material_released', name: 'Material Released', description: 'Sent when new class materials are unlocked', recipient: 'Student' },
  { id: 'low_attendance', name: 'Low Attendance Alert', description: "Sent when a student's attendance drops below 70%", recipient: 'Student' },
  { id: 'level_promotion', name: 'Level Promotion', description: 'Sent when a student is promoted to the next level', recipient: 'Student' },
  { id: 'new_enrollment_alert', name: 'New Enrollment Alert', description: 'Sent when a new student enrolls in your course', recipient: 'Teacher' }
];

const WHATSAPP_MESSAGES = [
  { id: 'class_reminder_wa', name: 'Class Reminder', description: 'Sent 24 hours before a scheduled class', recipient: 'Student' },
  { id: 'enrollment_wa', name: 'Enrollment Confirmation', description: 'Sent when a student enrolls', recipient: 'Student' },
  { id: 'material_released_wa', name: 'Material Released', description: 'Sent when new materials are available', recipient: 'Student' },
];

const NotificationsSettings = () => {
  const { studio, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('email');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [senderAddress, setSenderAddress] = useState('');
  const [senderName, setSenderName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [lifecycle, setLifecycle] = useState({
    class_reminder: true, payment_reminder: true, material_released: true,
    low_attendance: true, level_promotion: true, new_enrollment_alert: true
  });

  useEffect(() => {
    if (studio?.emailSettings) {
      setSenderAddress(studio.emailSettings.senderAddress || '');
      setSenderName(studio.emailSettings.senderName || studio.name || '');
      setReplyTo(studio.emailSettings.replyTo || '');
      setLifecycle(prev => ({ ...prev, ...studio.emailSettings.lifecycle }));
    } else if (studio) {
      setSenderName(studio.name || '');
      setSenderAddress(studio.slug ? `${studio.slug}@mydancehall.com` : '');
    }
  }, [studio]);

  const toggleLifecycle = (key) => {
    setLifecycle(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put('/studio', { emailSettings: { senderAddress, senderName, replyTo, lifecycle } });
      await refreshUser();
      setSuccess('Settings saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Notifications</h1>
      <p className="text-sm text-gray-500 mb-4">Configure how and when notifications are sent to your students.</p>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('email')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-2 ${
            activeTab === 'email' ? 'text-brand-500 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
          </svg>
          Email
        </button>
        <button
          onClick={() => setActiveTab('whatsapp')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-2 ${
            activeTab === 'whatsapp' ? 'text-green-600 border-green-500' : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
          </svg>
          WhatsApp
        </button>
      </div>

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* ─── Email Tab ──────────────────────────── */}
      {activeTab === 'email' && (
        <>
          {/* Sender Identity */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Sender Identity</h2>
            <p className="text-sm text-gray-500 mb-4">Students will see this name and address on emails from your studio.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sender Name</label>
                <input type="text" value={senderName} onChange={e => setSenderName(e.target.value)} placeholder="Enter your studio name" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
                <p className="text-xs text-gray-400 mt-1">Displayed as the "from" name in student inboxes</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sender Email Address</label>
                <input type="text" value={senderAddress} onChange={e => setSenderAddress(e.target.value)} placeholder="yourstudio@mydancehall.com" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
                <p className="text-xs text-gray-400 mt-1">Must end with @mydancehall.com</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reply-to Email (optional)</label>
                <input type="email" value={replyTo} onChange={e => setReplyTo(e.target.value)} placeholder="Enter your email address" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
                <p className="text-xs text-gray-400 mt-1">When students reply, replies go to this address</p>
              </div>
            </div>
          </div>

          {/* Transactional Emails */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold text-gray-900">Transactional Emails</h2>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Always On</span>
            </div>
            <p className="text-sm text-gray-500 mb-4">Required for your studio to function. Cannot be disabled.</p>
            <div className="divide-y divide-gray-50">
              {TRANSACTIONAL_EMAILS.map(email => (
                <div key={email.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{email.name}</p>
                    <p className="text-xs text-gray-500">{email.description}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{email.recipient}</span>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium">Active</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Lifecycle Emails */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Lifecycle Emails</h2>
            <p className="text-sm text-gray-500 mb-4">Automated emails triggered by student activity.</p>
            <div className="divide-y divide-gray-50">
              {LIFECYCLE_EMAILS.map(email => (
                <div key={email.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{email.name}</p>
                    <p className="text-xs text-gray-500">{email.description}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${email.recipient === 'Teacher' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>{email.recipient}</span>
                    <button
                      onClick={() => toggleLifecycle(email.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${lifecycle[email.id] ? 'bg-brand-500' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${lifecycle[email.id] ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={handleSave} disabled={saving} className="bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">
              {saving ? 'Saving...' : 'Save Email Settings'}
            </button>
          </div>
        </>
      )}

      {/* ─── WhatsApp Tab ───────────────────────── */}
      {activeTab === 'whatsapp' && (
        <>
          {/* Connection Status */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold text-gray-900">WhatsApp Business</h2>
              {studio?.whatsapp?.connected ? (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Connected</span>
              ) : (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Not Connected</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Connect your WhatsApp Business account to send class reminders and notifications directly to students.
            </p>

            {studio?.whatsapp?.connected ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-medium text-green-700">WhatsApp Connected</span>
                </div>
                <div className="text-sm text-gray-500 space-y-1 mb-4">
                  <div>Phone: {studio.whatsapp.phoneNumber || '—'}</div>
                  {studio.whatsapp.connectedAt && <div>Connected {new Date(studio.whatsapp.connectedAt).toLocaleDateString()}</div>}
                </div>
                <button className="text-sm text-red-500 hover:text-red-700 font-medium">
                  Disconnect WhatsApp
                </button>
              </div>
            ) : (
              <div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 mb-1">Connect WhatsApp Business</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Send class reminders, enrollment confirmations, and announcements directly to your students on WhatsApp.
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1.5 mb-5">
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                          Automatic class reminders 24h before
                        </li>
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                          Enrollment confirmations
                        </li>
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                          New material notifications
                        </li>
                      </ul>
                      <button
                        className="bg-green-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                        onClick={() => {
                          // TODO: Implement Meta Embedded Signup flow
                          // For now, show coming soon
                          alert('WhatsApp Business Embedded Signup coming soon. For now, class reminders are sent via DanceHall\'s WhatsApp number.');
                        }}
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                        </svg>
                        Connect WhatsApp Business
                      </button>
                    </div>
                  </div>
                </div>

                {/* Interim: DanceHall WhatsApp */}
                <div className="bg-white p-5 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-sm font-medium text-green-700">DanceHall WhatsApp Active</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    While your own WhatsApp isn't connected, class reminders are sent from DanceHall's verified WhatsApp number (+1 551-401-3075).
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp Message Types */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">WhatsApp Messages</h2>
            <p className="text-sm text-gray-500 mb-4">Messages sent to students via WhatsApp.</p>
            <div className="divide-y divide-gray-50">
              {WHATSAPP_MESSAGES.map(msg => (
                <div key={msg.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{msg.name}</p>
                    <p className="text-xs text-gray-500">{msg.description}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{msg.recipient}</span>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium">Active</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsSettings;
